/* eslint-disable import/no-extraneous-dependencies */
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import { Buffer } from 'buffer';
import { jwtDecode } from 'jwt-decode';
import { safeJsonParse } from '@rcw/utils';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

export const isSafari = navigator.vendor && navigator.vendor.indexOf('Apple') > -1
&& navigator.userAgent
&& navigator.userAgent.indexOf('CriOS') === -1
&& navigator.userAgent.indexOf('FxiOS') === -1;

export const formatCurrency = currencyFormatter.format;
export const isNumber = (data) => typeof data === 'number';
export const pickBy = (object) => {
  const obj = {};
  for (const key in object) {
    if (object[key]) {
      obj[key] = object[key];
    }
  }
  return obj;
};

export const goToCustomUrl = (url) => {
  window.location.href = url;
};

export const getUrlOffersParams = (location) => {
  let offerSKU = null;
  let offerId = null;
  let loc = null;

  const decodedParams = Buffer.from(location.search, 'base64').toString() || '';
  const hasCorrectParams = decodedParams.includes('offer') && decodedParams.includes('idx');
  const hasLocParams = decodedParams.includes('loc');
  const splitParams = decodedParams?.split('&');

  if (hasCorrectParams) {
    // check if has valid offer params
    const offer = head(splitParams.filter((el) => el.includes('offer=')));
    const idx = head(splitParams.filter((el) => el.includes('idx=')));
    // parse them appropriately
    offerSKU = !isEmpty(offer) && isString(offer)
      ? offer.replace('offer=', '')
      : null;
    offerId = !isEmpty(idx) && isString(idx)
      ? parseInt(idx.replace('idx=', ''), 10)
      : null;
  }

  // check if localizations are present
  if (hasLocParams) {
    const locParam = head(splitParams.filter((el) => el.includes('loc=')));
    loc = !isEmpty(locParam) && isString(locParam)
      ? locParam.replace('loc=', '')
      : null;
  }

  return { offerSKU, offerId, loc };
};

export const getLocalizationParam = (search) => {
  const urlParams = new URLSearchParams(search);
  const loc = urlParams.get('loc');

  return loc;
};

export const getLocalizationCopyContent = (loc, content) => {
  // check if there's content for that localization
  if (content && content[loc]) {
    return content[loc];
  }
  // else return the default content
  return content.en;
};

export const getScreenSize = (width) => {
  if (width < 320) {
    return 'xs';
  }
  if (width >= 320 && width < 720) {
    return 'sm';
  }
  if (width >= 720 && width < 1024) {
    return 'md';
  }
  if (width >= 1024 && width < 1440) {
    return 'lg';
  }
  if (width >= 1440) {
    return 'xl';
  }
};

export const goToLogin = (sku, offerIdx, loginAndRedirectUrl, locParam) => {
  const encodedParams = Buffer.from(`offer=${sku}&idx=${offerIdx}${locParam ? `&${locParam}` : ''}`).toString('base64');
  // fer: don't forget to add as slash before the encoded params
  // otherwise the params will be lost
  const baseUrl = loginAndRedirectUrl?.includes('?')
    ? loginAndRedirectUrl
    : loginAndRedirectUrl?.replace(/\/*$/, '/');
  const redirectTo = `${baseUrl}?${encodedParams}`;
  window.location.href = redirectTo;
};

/**
 * Determines if the current time falls between the given start and end dates.
 *
 * @param {boolean} enableCountdownBanner - Whether the countdown banner is enabled.
 * @param {string} startDate - The UTC start date in ISO 8601 format.
 * @param {string} endDate - The UTC end date in ISO 8601 format.
 * @returns {boolean} - True if the current time is within the range and the banner is enabled.
 */
export function shouldShowPromoSale(enableCountdownBanner, startDate, endDate) {
  if (!enableCountdownBanner) return false;

  const currentTime = new Date().getTime();
  const startTimeUTC = Date.parse(startDate);
  const endTimeUTC = Date.parse(endDate);

  return currentTime >= startTimeUTC && currentTime <= endTimeUTC;
}

export const isInTheCart = (productId) => {
  const cart = localStorage.getItem('cart');
  if (cart) {
    const parsedCart = safeJsonParse(cart, []);
    return parsedCart.some((p) => p.id === productId);
  }
  return false;
};

export const checkIfTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);

    if (!decoded.exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);

    return decoded.exp < currentTime;
  } catch (error) {
    console.error('Invalid token:', error);
    return true;
  }
};

export const slugify = (value) => {
  return value
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-');
};

export const checkIfProductIsNew = (ReleaseDate) => {
  if (!ReleaseDate) return false;

  const releaseDateMatch = ReleaseDate.match(/^(\d{4}-\d{2}-\d{2})(\+(\d+))?$/);
  if (!releaseDateMatch) return false;

  const releaseDate = new Date(releaseDateMatch[1]);
  const currentDate = new Date();

  // Clear time parts to compare by date only
  releaseDate.setHours(0, 0, 0, 0);
  currentDate.setHours(0, 0, 0, 0);

  if (releaseDateMatch[3]) {
    const daysVisible = parseInt(releaseDateMatch[3], 10);
    const expirationDate = new Date(releaseDate);
    expirationDate.setDate(expirationDate.getDate() + daysVisible);

    return currentDate >= releaseDate && currentDate <= expirationDate;
  }

  return currentDate.getTime() === releaseDate.getTime();
};

export function getOrderedSections(sections) {
  return Object.entries(sections)
    .map(([key, section]) => ({ key, ...section }))
    .filter((section) => section.enabled)
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))
    .map((section) => {
      if (Array.isArray(section.elements)) {
        return {
          ...section,
          elements: section.elements
            .filter((el) => el.enabled)
            .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity)),
        };
      }
      return section;
    });
}

export function formatDate(date) {
  return new Date(date)
    .toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
}

/**
 * Determines if a reward rule identifier is usable in a claim request.
 *
 * Rules carry a UserRuleId and/or a UserSubscriptionRuleId, and either may be
 * absent. An absent id must be omitted from the payload rather than stringified,
 * since String(undefined) yields "undefined", which the API would treat as a
 * real id.
 *
 * @param {*} value - The identifier to check.
 * @returns {boolean} - True if the value can be sent as an identifier.
 *
 * @example
 * hasRewardId(69014)     // → true
 * hasRewardId('69014')   // → true
 * hasRewardId(undefined) // → false
 * hasRewardId(null)      // → false
 * hasRewardId(0)         // → false
 */
export const hasRewardId = (value) => value !== null
  && value !== undefined
  && value !== ''
  && value !== 0
  && value !== '0';
